import { createContext, useContext, ReactNode } from 'react'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'
import type { ChatMode } from '@/types/message'

export interface RespondingAsNpc {
  isActive: boolean
  npcUserId: string | null
  npcName: string | null
}

export interface MediaData {
  id: string
  url: string
  filename: string
  type: string
  size: number
}

export interface ChatContextValue {
  // Estado do chat
  // (messageInput/privateMessageInput saíram — Fase 11: o texto digitado é
  // estado local do ChatInputContainer; o envio passa o texto por argumento.
  // Assim digitar não re-renderiza o GameSession nem os consumidores do contexto.)
  chatMode: ChatMode

  // Selecao
  selectedCharacter: Character | null
  selectedPrivateUser: MemberType | null
  selectedMediaFile: string | null
  selectedMediaData: MediaData | null

  // NPC response mode
  respondingAsNpc: RespondingAsNpc

  // Estado de conexao
  isConnected: boolean
  isReconnecting: boolean
  loadingHistory: boolean
  loadingPrivateHistory: boolean
  isMaster: boolean

  // Acoes de envio — retornam sucesso para o input decidir se limpa o texto
  onSendMessage: (text: string) => boolean
  onSendPrivateMessage: (text: string) => Promise<boolean>

  // Acoes de selecao
  onSelectCharacter: (character: Character | null) => void
  onMediaUpload: (mediaFile: MediaData) => void
  onToggleRespondAsNpc: (npc: Character | null) => void

  // Acoes de jogo
  onRollDice: () => void
  onLiveSync: () => void
  onPoll: () => void
  onLab: () => void

  // Modulos ativados na mesa (gating das acoes rapidas — regra 2026-07-23:
  // acao de modulo so aparece se o mestre ativou o modulo no gerenciador)
  enabledModules: string[]

  /**
   * Sessao da missao ativa (2026-07-27). A `MissionSessionBar` saiu da coluna
   * do chat: o titulo subiu para a barra do topo e estes dois controles vieram
   * para o painel de acoes rapidas, que e o receptaculo do que sai da moldura.
   *
   * `sessaoAberta` e `null` quando nao ha missao ativa — nesse caso a linha nao
   * aparece no painel.
   */
  sessaoDaMissao: {
    aberta: boolean
    onIniciar: () => Promise<void>
    onEncerrar: () => Promise<void>
  } | null

  // Metadata
  tableId: string
}

const ChatContext = createContext<ChatContextValue | null>(null)

interface ChatProviderProps {
  children: ReactNode
  value: ChatContextValue
}

export function ChatProvider({ children, value }: ChatProviderProps) {
  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}

export function useChatContext(): ChatContextValue {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider')
  }
  return context
}

// Hook opcional que retorna null se nao estiver dentro do provider
export function useChatContextOptional(): ChatContextValue | null {
  return useContext(ChatContext)
}
